import { createFileRoute } from "@tanstack/react-router";
import { Bullets, LegalPage, Note, Section, Value } from "@/components/legal-page";
import { LEGAL, NEVER_DO, PROCESSORS } from "@/lib/legal";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Xamseen — Privacy Policy" },
      {
        name: "description",
        content:
          "How Xamseen collects, uses, shares and protects information. We do not sell, rent or broker personal data, and we do not build advertising profiles.",
      },
      { property: "og:title", content: "Xamseen — Privacy Policy" },
      { property: "og:description", content: "What Xamseen collects, why we process it, and the controls you have." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://xamseen.com/privacy" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://xamseen.com/privacy" }],
  }),
  component: PrivacyPage,
});

const TOC = [
  { id: "who", label: "Who we are and how to contact us" },
  { id: "collect", label: "What information we collect" },
  { id: "why", label: "Why we process information" },
  { id: "never", label: "What we do not do" },
  { id: "sharing", label: "How information is shared" },
  { id: "retention", label: "How long we keep information" },
  { id: "security", label: "Security" },
  { id: "rights", label: "Your rights and controls" },
  { id: "international", label: "International users and transfers" },
  { id: "children", label: "Age requirement" },
  { id: "cookies", label: "Cookies and similar technologies" },
  { id: "changes", label: "Changes to this policy" },
];

function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy Policy"
      intro="Xamseen exists so communities can document real problems and turn them into action. That only works if people trust what happens to what they share. This policy explains, in plain language, what we collect, why we process it, who can see it, and what you control."
      toc={TOC}
    >
      <Note>
        The short version: Xamseen is not an advertising or data-selling business. We process the data we genuinely
        need in order to run the service, publish community reports the way you chose to share them, keep accounts
        secure and turn many individual observations into useful patterns — and we prefer aggregated or de-identified
        information whenever identifying a person is not necessary.
      </Note>

      <Section id="who" title="1. Who we are and how to contact us">
        <p>
          Xamseen is a global community reporting platform operated by{" "}
          <Value value={LEGAL.legalEntity} label="registered legal entity name" />, reachable for legal notices at{" "}
          <Value value={LEGAL.postalAddress} label="registered or postal address" />
          {LEGAL.registrationNumber ? ` (registration number ${LEGAL.registrationNumber})` : null}.
        </p>
        <p>
          For privacy questions or to make a request about your information, contact{" "}
          <Value value={LEGAL.privacyContactEmail} label="privacy contact email address" />. Where we are required to
          appoint a data protection officer or representative, that role is held by{" "}
          <Value value={LEGAL.dataProtectionOfficer} label="DPO or representative, or a statement that none is appointed" />
          .
        </p>
      </Section>

      <Section id="collect" title="2. What information we collect">
        <p>Almost everything below is information you actively give us. We do not buy personal data about you.</p>
        <Bullets
          items={[
            <>
              <strong>Account information</strong> — your email address, username, and the password hash held by our
              authentication provider. If you sign in with Google, we receive your basic profile information from
              Google rather than a password.
            </>,
            <>
              <strong>Profile information</strong> — the display name, bio, avatar, cover image, website, account type,
              language, timezone and location description you choose to add. All of this is optional beyond a username.
            </>,
            <>
              <strong>Reports and community content</strong> — the descriptions, photos, videos and any other media you
              submit, plus posts, questions, answers, comments, confirmations, votes, bookmarks and project or Space
              participation.
            </>,
            <>
              <strong>Location information</strong> — coordinates and a place name attached to a report, only when you
              choose to use a location feature and your device grants permission. You control the precision Xamseen
              stores in Settings, including turning coordinate storage off entirely.
            </>,
            <>
              <strong>Notification and communication preferences</strong> — your in-app, email, push and digest
              choices, and unsubscribe or suppression records.
            </>,
            <>
              <strong>Technical information needed to operate and secure the service</strong> — IP address, browser and
              device characteristics, request and error logs, and security events such as sign-in attempts.
            </>,
            <>
              <strong>Support and contact messages</strong> — what you send us when you report a problem, appeal a
              moderation decision or make a privacy request.
            </>,
            <>
              <strong>Moderation records</strong> — reports of abusive content, moderation decisions and enforcement
              history, kept so decisions can be reviewed and appealed.
            </>,
          ]}
        />
        <p>
          Video you attach to a post, report or official record is stored privately. It is never served from a public,
          guessable address: playback happens through a short-lived link that is issued only after we re-check that the
          record it belongs to is visible to the person watching. Camera and microphone access is requested only at the
          moment you deliberately start a recording, we do not read device metadata beyond the file itself, and deleting
          your video removes the stored file along with the poster frame taken from it. Audio you attach is treated the
          same way as other media you submit and is covered by this policy and by your visibility choices.
        </p>
      </Section>

      <Section id="why" title="3. Why we process information">
        <Bullets
          items={[
            <>To provide Xamseen: create your account, show your feed, publish and display community content.</>,
            <>To authenticate you and keep your account secure, including verification and password reset.</>,
            <>
              To publish and process community reports according to the visibility and precision choices you made, and
              to move them through confirmation, status tracking and resolution.
            </>,
            <>To connect you with the communities, Spaces, projects and areas you choose to follow.</>,
            <>
              To identify patterns across many reports — repeated locations, recurring categories, hotspots — and turn
              them into actionable community insight. Wherever the insight does not require identifying an individual,
              we use aggregated, anonymised or de-identified information.
            </>,
            <>
              To send service communications: verification, password resets, security alerts, activity notifications and
              digests you opted into. These are operational messages, not advertising.
            </>,
            <>To prevent, detect and respond to abuse, spam, false or malicious reports, fraud and security incidents.</>,
            <>To keep the service reliable, accessible and fast, including diagnosing errors and outages.</>,
            <>To comply with legal obligations that apply to us, and to establish or defend legal claims.</>,
          ]}
        />
        <p>
          The legal basis for each purpose depends on where you are and is being confirmed with legal counsel before
          launch; we will state the applicable bases in this section rather than guess at them.
        </p>
      </Section>

      <Section id="never" title="4. What we do not do">
        <Bullets items={NEVER_DO.map((n) => <>{n}</>)} />
        <p>
          What we do instead is narrower and more honest: we process the data needed to run Xamseen, and we surface
          patterns from community reports so the people who can fix a problem can see it.
        </p>
      </Section>

      <Section id="sharing" title="5. How information is shared">
        <p>
          <strong>Publicly, because you chose to publish it.</strong> Community content — reports, posts, answers,
          comments and the profile fields you filled in — is visible to others according to the visibility and
          location-precision settings that applied when you posted it. Treat anything you post as something other people
          will read.
        </p>
        <p>
          <strong>With service providers who process data on our behalf.</strong> They may only use it to deliver their
          service to us, under contract, and not for their own purposes:
        </p>
        <div className="overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-left text-sm">
            <thead className="bg-surface text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-semibold">Function</th>
                <th className="px-4 py-3 font-semibold">What it does</th>
                <th className="px-4 py-3 font-semibold">Data involved</th>
              </tr>
            </thead>
            <tbody>
              {PROCESSORS.map((p) => (
                <tr key={p.name} className="border-t border-border align-top">
                  <td className="px-4 py-3 font-medium">{p.name}</td>
                  <td className="px-4 py-3 text-foreground/80">{p.role}</td>
                  <td className="px-4 py-3 text-muted-foreground">{p.data}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm text-muted-foreground">
          The named vendors behind each function are being finalised and will be listed on this page before launch.
        </p>
        <p>
          <strong>With authorities, organisations and community stakeholders.</strong> Xamseen only shares report
          information onward where that is consistent with the purpose of the report, the visibility you chose,
          applicable law, a genuine safety requirement, or appropriate authorisation. Reporting an issue does not
          automatically expose your private personal information. Where a pattern is what matters — say, repeated
          flooding at one junction — we share the aggregated picture rather than individual identities.
        </p>
        <p>
          <strong>For legal and safety reasons.</strong> We may disclose information where we are legally required to,
          or where it is necessary to protect someone's life or safety, prevent serious harm, or protect our rights.
        </p>
        <p>
          <strong>In a business transfer.</strong> If Xamseen is ever involved in a merger, acquisition or transfer of
          assets, information may transfer with it, and this policy continues to apply until you are told otherwise.
        </p>
      </Section>

      <Section id="retention" title="6. How long we keep information">
        <p>
          We keep information only as long as reasonably necessary for the purpose it was collected for, plus any period
          required for legal obligations, safety, security investigations, dispute resolution or legitimate operational
          needs.
        </p>
        <Bullets
          items={[
            <>Account and profile data is kept while your account exists.</>,
            <>
              Reports and community content remain published while relevant to the community and to the tracking of the
              issue. Deleting your account removes your account and profile; where a report has become part of a shared
              civic record, we may retain a de-identified version of it.
            </>,
            <>
              Security, error and moderation logs are kept for a limited period appropriate to detecting and
              investigating abuse.
            </>,
            <>
              Email suppression records (unsubscribes, bounces) are kept for as long as needed to keep honouring your
              choice.
            </>,
          ]}
        />
        <p>
          Exact retention periods per category are being finalised with legal review before launch and will be published
          here rather than approximated.
        </p>
      </Section>

      <Section id="security" title="7. Security">
        <Bullets
          items={[
            <>Traffic between your device and Xamseen is encrypted in transit using HTTPS.</>,
            <>
              Access to data is enforced at the database level with row-level security policies, so a request can only
              read or change what that account is permitted to.
            </>,
            <>
              Authentication is handled by a managed provider; passwords are stored as hashes and never in readable
              form, and we support verification and password reset flows with expiring links.
            </>,
            <>Privileged administrative access is limited to what is necessary to operate and moderate the service.</>,
            <>We monitor application errors and security events so we can detect and respond to problems quickly.</>,
          ]}
        />
        <p>
          No online service can promise perfect security, and we will not pretend otherwise. If a breach affects your
          information and we are required to notify you, we will.
        </p>
      </Section>

      <Section id="rights" title="8. Your rights and controls">
        <p>
          Depending on where you live, you may have the right to access your information, correct it, delete it, obtain
          a portable copy, restrict or object to certain processing, withdraw consent where consent is the basis we rely
          on, and complain to your local data protection authority.
        </p>
        <p>You can exercise the main ones yourself, right now, in Xamseen:</p>
        <Bullets
          items={[
            <>
              <strong>Access and portability</strong> — Settings → Privacy & your data → Download my data exports your
              profile, posts, comments, reports and preferences as a JSON file.
            </>,
            <>
              <strong>Correction</strong> — edit your profile and preferences in Settings at any time.
            </>,
            <>
              <strong>Deletion</strong> — Settings → Privacy & your data → Delete my account permanently deletes your
              account, profile and your content.
            </>,
            <>
              <strong>Location minimisation</strong> — Settings → Privacy & your data → Location precision controls how
              precisely coordinates are stored on new reports, including not storing them at all.
            </>,
            <>
              <strong>Communication choices</strong> — Settings → Notifications controls in-app and email notifications
              per category. Security and account emails are always sent because they protect your account.
            </>,
          ]}
        />
        <p>
          For anything not covered by those controls, contact{" "}
          <Value value={LEGAL.privacyContactEmail} label="privacy contact email address" />.
        </p>
      </Section>

      <Section id="international" title="9. International users and transfers">
        <p>
          Xamseen is used globally, and our hosting, database, storage and email providers may process information in
          countries other than the one you live in. When information moves across borders we rely on the safeguards our
          providers make available, such as contractual data protection terms and provider-level transfer mechanisms.
        </p>
        <p>
          The specific transfer mechanisms and processing regions that apply to Xamseen are being confirmed with legal
          counsel before launch, and will be described here precisely instead of in general terms.
        </p>
      </Section>

      <Section id="children" title="10. Age requirement">
        <p>
          Xamseen is not intended for children. You must be at least {LEGAL.minimumAge} years old to create an account,
          or older where your country requires a higher age for this kind of service. We do not knowingly collect
          personal data from children below the applicable age.
        </p>
        <p>
          If you believe a child has created an account or that we hold a child's personal data, contact{" "}
          <Value value={LEGAL.privacyContactEmail} label="privacy contact email address" /> and we will remove it.
        </p>
      </Section>

      <Section id="cookies" title="11. Cookies and similar technologies">
        <p>
          Xamseen uses only what it needs to work: a session record so you stay signed in, and small local preferences
          such as your theme choice. There are no advertising cookies, ad pixels or third-party tracking SDKs in
          Xamseen. The full list is on the cookies page.
        </p>
      </Section>

      <Section id="changes" title="12. Changes to this policy">
        <p>
          When this policy changes we update the date at the top of the page. If a change materially affects how we use
          your information, we will tell you in the product or by email before it takes effect, where that is
          appropriate.
        </p>
      </Section>

      <Note tone="warn">
        Legal review status: these pages describe how Xamseen is actually built and operated. The items marked as
        pending — legal entity, address, contacts, governing law, exact retention periods and named subprocessors — must
        be supplied and reviewed by qualified counsel before public launch. Xamseen does not claim certification under,
        or compliance with, any specific privacy law on the basis of this page alone.
      </Note>
    </LegalPage>
  );
}
